import { forwardRef } from "react";
import { createPortal } from "react-dom";
import {
  Button,
  Dropdown,
  Form,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { ChevronRight } from "lucide-react";
import { TableSkeletonRows } from "../loader";
import { TablePagination } from "../common/TablePagination";
import { EmptyTableState } from "../common/EmptyTableState";
import { StatusBadge } from "../common/StatusBadge";
import {
  StyledTable,
  StyledTableHeader,
  StyledTableCell,
  StyledTableRow,
} from "../common/StyledTable";

const ActionToggle = forwardRef(({ onClick }, ref) => (
  <Button
    ref={ref}
    size="sm"
    variant="light"
    className="action-toggle-btn"
    onClick={(e) => {
      e.preventDefault();
      onClick(e);
    }}
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      fill="currentColor"
      viewBox="0 0 16 16"
    >
      <path d="M9.5 13a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0m0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0m0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0" />
    </svg>
  </Button>
));

function CustomerAccountsTable({
  data,
  listLoading,
  pageSize,
  rowActionLoading,
  onChangeStatus,
  onDeleteUser,
  onOpenLimitModal,
  onNavigateToAgents,
  onOpenUserInfo,
  page,
  totalItems,
  onPageChange,
}) {
  return (
    <>
      <StyledTable
        responsive="sm"
        className="UserListTable premium-user-list-table customer-accounts-table"
      >
        <colgroup>
          <col className="customer-account-name-col" />
          <col className="customer-account-email-col" />
          <col className="customer-account-subscription-col" />
          <col className="customer-account-status-col" />
          <col className="customer-account-action-col" />
        </colgroup>
        <thead>
          <StyledTableRow>
            <StyledTableHeader>Name</StyledTableHeader>
            <StyledTableHeader>Email</StyledTableHeader>
            <StyledTableHeader>Subscription</StyledTableHeader>
            <StyledTableHeader>Status</StyledTableHeader>
            <StyledTableHeader>Action</StyledTableHeader>
          </StyledTableRow>
        </thead>
        <tbody>
          {listLoading ? (
            <TableSkeletonRows rows={pageSize} columns={5} />
          ) : data.length > 0 ? (
            data.map((u) => (
              <StyledTableRow key={u._id}>
                <StyledTableCell>
                  <button
                    type="button"
                    className="user-name-link"
                    onClick={() => onOpenUserInfo(u)}
                  >
                    {u.name || "-"}
                  </button>
                </StyledTableCell>
                <StyledTableCell>{u.email || "-"}</StyledTableCell>
                <StyledTableCell>
                  <div className="subscription-cell">
                    <span className="subscription-type">
                      {u.currentSubscription?.type || u.subscription?.type || "-"}
                    </span>
                    {u.currentSubscription && (
                      <StatusBadge
                        label={u.currentSubscription.label}
                        tone={u.currentSubscription.tone}
                      />
                    )}
                  </div>
                </StyledTableCell>
                <StyledTableCell>
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip>
                        Click to {u.is_active ? "deactivate" : "activate"} user
                      </Tooltip>
                    }
                  >
                    <Form.Check
                      type="switch"
                      id="custom-switch"
                      checked={u.is_active}
                      disabled={rowActionLoading[u._id]}
                      onChange={() => onChangeStatus(u._id, u.is_active)}
                    />
                  </OverlayTrigger>
                </StyledTableCell>
                <StyledTableCell>
                  <div className="table-row-actions">
                    <Dropdown>
                      <Dropdown.Toggle
                        as={ActionToggle}
                        disabled={rowActionLoading[u._id]}
                      />
                      {createPortal(
                        <Dropdown.Menu
                          align="end"
                          className="action-dropdown-menu"
                        >
                          <Dropdown.Item onClick={() => onNavigateToAgents(u)}>
                            <i
                              className="fa fa-list me-2"
                              aria-hidden="true"
                            ></i>
                            Agents
                          </Dropdown.Item>
                          {/* <Dropdown.Item onClick={() => onOpenLimitModal(u)}>
                            <i
                              className="fa fa-tachometer me-2"
                              aria-hidden="true"
                            ></i>
                            Change Limit
                          </Dropdown.Item> */}

                          <Dropdown.Divider />

                          <Dropdown.Item onClick={() => onDeleteUser(u._id)}>
                            <i
                              className="fa fa-trash me-2"
                              aria-hidden="true"
                            ></i>
                            Delete
                          </Dropdown.Item>
                        </Dropdown.Menu>,
                        document.body,
                      )}
                    </Dropdown>
                    <button
                      type="button"
                      className="user-row-chevron-btn"
                      aria-label={`View profile for ${u.name || "user"}`}
                      onClick={() => onOpenUserInfo(u)}
                    >
                      <ChevronRight size={16} strokeWidth={2} aria-hidden="true" />
                    </button>
                  </div>
                </StyledTableCell>
              </StyledTableRow>
            ))
          ) : (
            <EmptyTableState colSpan={5} />
          )}
        </tbody>
      </StyledTable>
      <TablePagination
        activePage={page}
        itemsCountPerPage={pageSize}
        totalItemsCount={totalItems}
        onChange={onPageChange}
      />
    </>
  );
}

export default CustomerAccountsTable;
